import { useEffect, useState } from "react"

/**
 * Tailwind's `md` breakpoint is 768px; "mobile" here means anything narrower.
 * Keep this in sync with the `md:` classes used in the layout shell.
 */
export const MOBILE_MEDIA_QUERY = "(max-width: 767px)"

function getMatch(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false
  }
  return window.matchMedia(MOBILE_MEDIA_QUERY).matches
}

/**
 * Returns `true` when the viewport is narrower than Tailwind's `md`
 * breakpoint (< 768px). SSR-safe (returns `false` without a window) and
 * updates live on resize / orientation change.
 *
 * Prefer CSS breakpoints (`md:`) for styling; reach for this hook only when
 * the DOM structure itself has to differ between phone and desktop.
 */
export function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState<boolean>(getMatch)

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return
    }
    const mql = window.matchMedia(MOBILE_MEDIA_QUERY)
    const onChange = (event: MediaQueryListEvent) => setIsMobile(event.matches)
    // Sync in case the viewport changed between render and effect.
    setIsMobile(mql.matches)
    if (typeof mql.addEventListener === "function") {
      mql.addEventListener("change", onChange)
      return () => mql.removeEventListener("change", onChange)
    }
    // Safari < 14 fallback.
    mql.addListener(onChange)
    return () => mql.removeListener(onChange)
  }, [])

  return isMobile
}

export default useIsMobile
